import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Loader2 } from 'lucide-react'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'
import { z } from 'zod'
import { Button } from '@/components/ui/button'
import {
	Dialog,
	DialogContent,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { adminApi, type AdminBranchDetail } from '@/lib/admin-api'

const formSchema = z.object({
	branchName: z.string(),
	ico: z.string().min(1, 'Required'),
	departmentId: z.string(),
	fb_ipaddress: z.string(),
	fb_port: z.string(),
	fb_databasePathName: z.string(),
	fb_username: z.string(),
	fb_password: z.string(),
})

type FormValues = z.infer<typeof formSchema>

interface BranchEditDialogProps {
	branch: AdminBranchDetail
	open: boolean
	onClose: () => void
}

export function BranchEditDialog({ branch, open, onClose }: BranchEditDialogProps) {
	const queryClient = useQueryClient()

	const form = useForm<FormValues>({
		resolver: zodResolver(formSchema),
		defaultValues: {
			branchName: branch.branchName ?? '',
			ico: branch.ico,
			departmentId: branch.departmentId?.toString() ?? '',
			fb_ipaddress: branch.fb_ipaddress ?? '',
			fb_port: branch.fb_port?.toString() ?? '',
			fb_databasePathName: branch.fb_databasePathName ?? '',
			fb_username: branch.fb_username ?? '',
			fb_password: branch.fb_password ?? '',
		},
	})

	const mutation = useMutation({
		mutationFn: (values: FormValues) => {
			const departmentId = values.departmentId ? parseInt(values.departmentId) : undefined
			const fb_port = values.fb_port ? parseInt(values.fb_port) : undefined
			return adminApi.updateBranch(branch.branchId, {
				branchName: values.branchName || undefined,
				ico: values.ico,
				departmentId,
				fb_ipaddress: values.fb_ipaddress || undefined,
				fb_port,
				fb_databasePathName: values.fb_databasePathName || undefined,
				fb_username: values.fb_username || undefined,
				fb_password: values.fb_password || undefined,
			})
		},
		onSuccess: (updated) => {
			queryClient.setQueryData(['admin', 'branches', branch.branchId], updated)
			queryClient.invalidateQueries({ queryKey: ['admin', 'branches'] })
			toast.success('Branch updated')
			onClose()
		},
		onError: (err) => {
			toast.error(err instanceof Error ? err.message : 'Update failed')
		},
	})

	function handleOpenChange(open: boolean) {
		if (!open) onClose()
	}

	return (
		<Dialog open={open} onOpenChange={handleOpenChange}>
			<DialogContent className="bg-white sm:max-w-lg [&_input]:bg-white [&_input]:text-gray-900 [&_input]:border-gray-300">
				<DialogHeader>
					<DialogTitle className="text-gray-900">Edit Branch #{branch.branchId}</DialogTitle>
				</DialogHeader>

				<form
					onSubmit={form.handleSubmit((v) => mutation.mutate(v))}
					className="space-y-4 mt-2"
				>
					<div className="grid grid-cols-2 gap-3">
						<FormField
							label="Branch Name"
							error={form.formState.errors.branchName?.message}
							className="col-span-2"
						>
							<Input
								placeholder="e.g. Praha 1"
								{...form.register('branchName')}
							/>
						</FormField>

						<FormField label="IČO" error={form.formState.errors.ico?.message}>
							<Input
								placeholder="12345678"
								{...form.register('ico')}
							/>
						</FormField>

						<FormField
							label="Department ID"
							error={form.formState.errors.departmentId?.message}
						>
							<Input
								type="number"
								placeholder="—"
								{...form.register('departmentId')}
							/>
						</FormField>
					</div>

					<div className="border-t border-gray-100 pt-4">
						<p className="text-xs font-semibold uppercase tracking-wider text-gray-400 mb-3">
							Firebird Connection
						</p>
						<div className="space-y-3">
							<div className="grid grid-cols-3 gap-3">
								<FormField
									label="IP Address"
									error={form.formState.errors.fb_ipaddress?.message}
									className="col-span-2"
								>
									<Input
										placeholder="192.168.1.1"
										{...form.register('fb_ipaddress')}
									/>
								</FormField>

								<FormField
									label="Port"
									error={form.formState.errors.fb_port?.message}
								>
									<Input
										type="number"
										placeholder="3050"
										{...form.register('fb_port')}
									/>
								</FormField>
							</div>

							<FormField
								label="Database Path"
								error={form.formState.errors.fb_databasePathName?.message}
							>
								<Input
									placeholder="C:/path/to/database.fdb"
									{...form.register('fb_databasePathName')}
								/>
							</FormField>

							<div className="grid grid-cols-2 gap-3">
								<FormField
									label="Username"
									error={form.formState.errors.fb_username?.message}
								>
									<Input
										placeholder="SYSDBA"
										{...form.register('fb_username')}
									/>
								</FormField>

								<FormField
									label="Password"
									error={form.formState.errors.fb_password?.message}
								>
									<Input
										type="password"
										placeholder="••••••••"
										{...form.register('fb_password')}
									/>
								</FormField>
							</div>
						</div>
					</div>

					<DialogFooter className="pt-2">
						<Button
							type="button"
							variant="outline"
							onClick={onClose}
							disabled={mutation.isPending}
						>
							Cancel
						</Button>
						<Button type="submit" disabled={mutation.isPending}>
							{mutation.isPending && (
								<Loader2 className="h-4 w-4 mr-2 animate-spin" />
							)}
							Save Changes
						</Button>
					</DialogFooter>
				</form>
			</DialogContent>
		</Dialog>
	)
}

function FormField({
	label,
	error,
	className,
	children,
}: {
	label: string
	error?: string
	className?: string
	children: React.ReactNode
}) {
	return (
		<div className={className}>
			<Label className="text-xs text-gray-600 mb-1.5 block">{label}</Label>
			{children}
			{error && <p className="text-xs text-red-600 mt-1">{error}</p>}
		</div>
	)
}
